import { apiRequest, buildApiUrl, getHeaders } from '../utils/api';

export const MAX_HEATMAP_WINDOW_DAYS = 31;

/**
 * Normalizes an ETag string so it is properly enclosed in double quotes for If-Match.
 * @param {string} etag 
 * @returns {string}
 */
export const normalizeIfMatchHeader = (etag) => {
  if (!etag) return '';
  const trimmed = String(etag).trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed;
  }
  return `"${trimmed}"`;
};

/**
 * Extracts ETag value from a fetch Response object.
 * @param {Response} response 
 * @returns {string|null}
 */
export const extractEtag = (response) => {
  if (!response || !response.headers) return null;
  return response.headers.get('ETag') || response.headers.get('etag') || null;
};

/**
 * Validates a date range for Heatmap & Customer Analytics (up to 31 calendar days).
 * Does not mutate or interfere with the 7-day safety limit of legacy telemetry.
 * @param {string} fromDate - Start date (YYYY-MM-DD or ISO string)
 * @param {string} toDate - End date (YYYY-MM-DD or ISO string)
 */
export const validateHeatmapDateRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) {
    throw new Error('Invalid date range: fromDate and toDate are required');
  }

  const start = new Date(fromDate);
  const end = new Date(toDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    throw new Error('Invalid date format: dates must be valid ISO or YYYY-MM-DD strings');
  }

  if (start >= end) {
    throw new Error('Invalid date range: fromDate must be earlier than toDate');
  }

  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  // Backend limit: maximum 31 days window (with small float tolerance for time bounds)
  if (diffDays > 31.05) {
    throw new Error('Invalid date range: window cannot exceed 31 days (backend safety limit)');
  }
};

/**
 * Converts a date to an exclusive upper bound by incrementing by 1 UTC day.
 * Useful because the backend toDate parameter is strictly exclusive (< toDate).
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @returns {string} Date string incremented by 1 day (YYYY-MM-DD)
 */
export const toExclusiveToDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().split('T')[0];
};

/**
 * Detects whether an API error represents an EntitlementDenied (403) response.
 * @param {Error|object} error 
 * @returns {boolean}
 */
export const isHeatmapEntitlementDenied = (error) => {
  if (!error) return false;
  return (
    error.status === 403 ||
    (typeof error.message === 'string' && (
      error.message.includes('403') ||
      error.message.toLowerCase().includes('forbidden') ||
      error.message.toLowerCase().includes('entitlement') ||
      error.message.toLowerCase().includes('licensed')
    ))
  );
};

/**
 * Fetches current Heatmap settings and returns configuration along with the ETag.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @returns {Promise<{ settings: object, etag: string|null }>}
 */
export const getHeatmapSettings = async (organizationId, siteCollectionId, options = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/settings`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders(),
    ...(options?.signal ? { signal: options.signal } : {})
  });

  const etag = extractEtag(response);
  const settings = await response.json();

  return { settings, etag };
};

/**
 * Updates Heatmap settings enforcing optimistic concurrency via If-Match header.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} payload - { captureEnabled, advancedCaptureEnabled, pages, actions }
 * @param {string} [etag] - Current ETag from getHeatmapSettings
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ data: object, etag: string|null }>}
 */
export const updateHeatmapSettings = async (organizationId, siteCollectionId, payload, etag, options = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!payload || typeof payload !== 'object') throw new Error('Settings payload is required');

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/settings`;
  const headers = getHeaders();

  if (etag) {
    headers['If-Match'] = normalizeIfMatchHeader(etag);
  }

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'PUT',
    headers,
    body: JSON.stringify(payload),
    ...(options?.signal ? { signal: options.signal } : {})
  });

  const newEtag = extractEtag(response);
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }

  return { data, etag: newEtag };
};

/**
 * Retries an analytics API request once after 400ms if the backend distributed lock
 * is temporarily held by a concurrent query ("temporarily blocked").
 * @param {Function} apiCallFn 
 * @param {AbortSignal} [signal] 
 * @returns {Promise<any>}
 */
export const withDistributedLockRetry = async (apiCallFn, signal) => {
  try {
    return await apiCallFn();
  } catch (err) {
    const isTransientLock =
      err?.status === 409 &&
      typeof err?.message === 'string' &&
      err.message.toLowerCase().includes('temporarily blocked');

    if (isTransientLock && !signal?.aborted) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      if (signal?.aborted) {
        const abortErr = new Error('The user aborted a request.');
        abortErr.name = 'AbortError';
        throw abortErr;
      }
      return await apiCallFn();
    }

    throw err;
  }
};

/**
 * Fetches high-level Heatmap interaction and session summary with daily breakdown.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {string} fromDate - Start date (YYYY-MM-DD)
 * @param {string} toDate - Exclusive end date (YYYY-MM-DD)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ totalInteractions: number, uniqueSessions: number, captureObserved: boolean, daily: Array }>}
 */
export const getHeatmapSummary = async (organizationId, siteCollectionId, fromDate, toDate, options = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({ fromDate, toDate });
  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/summary?${params.toString()}`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getHeaders(),
      ...(options?.signal ? { signal: options.signal } : {})
    });
    return response.json();
  }, options?.signal);
};

/**
 * Fetches daily interaction trend points for a given date window.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {string} fromDate - Start date (YYYY-MM-DD)
 * @param {string} toDate - Exclusive end date (YYYY-MM-DD)
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array<{ date: string, totalInteractions: number, uniqueSessions: number }>>}
 */
export const getHeatmapDaily = async (organizationId, siteCollectionId, fromDate, toDate, options = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({ fromDate, toDate });
  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/daily?${params.toString()}`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getHeaders(),
      ...(options?.signal ? { signal: options.signal } : {})
    });
    return response.json();
  }, options?.signal);
};

/**
 * Fetches 2% coordinate bucket density grid for canvas heatmap rendering.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {string} options.pageId - Registered page UUID
 * @param {number} [options.layoutVersion=1] - Page layout version
 * @param {string} options.fromDate - Start date (YYYY-MM-DD)
 * @param {string} options.toDate - Exclusive end date (YYYY-MM-DD)
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array<{ xPercent: number, yPercent: number, count: number }>>}
 */
export const getHeatmapDensity = async (organizationId, siteCollectionId, {
  pageId,
  layoutVersion = 1,
  fromDate,
  toDate,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!pageId) throw new Error('pageId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({
    pageId,
    layoutVersion: String(layoutVersion),
    fromDate,
    toDate
  });

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/density?${params.toString()}`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};

/**
 * Fetches the one-time mirrored HTML DOM snapshot for a registered page.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {string} pageId 
 * @param {number} [layoutVersion=1] 
 * @param {AbortSignal} [signal] 
 * @returns {Promise<{ pageId: string, layoutVersion: number, url: string, htmlSnapshot: string, viewportWidth: number, viewportHeight: number } | null>}
 */
export const getPageSnapshot = async (organizationId, siteCollectionId, pageId, layoutVersion = 1, signal) => {
  if (!organizationId || !siteCollectionId || !pageId) return null;
  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/pages/${encodeURIComponent(pageId)}/snapshot?layoutVersion=${layoutVersion}`;
  const fullUrl = buildApiUrl(endpoint);
  console.log('[heatmap.service] getPageSnapshot calling:', fullUrl);

  try {
    const response = await apiRequest(fullUrl, {
      method: 'GET',
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    console.log('[heatmap.service] getPageSnapshot response status:', response.status);
    if (response.status === 404) return null;
    return await response.json();
  } catch (err) {
    console.warn('[heatmap.service] getPageSnapshot catch:', err?.message, err?.status);
    if (err?.status === 404 || err?.message?.includes('404')) return null;
    throw err;
  }
};

/**
 * Fetches the Replay Policy and effective capability status for a site collection.
 * API #7: GET /api/v1/heatmap/organizations/{orgId}/site-collections/{siteId}/replay/policy
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   organizationId: string,
 *   siteCollectionId: string,
 *   heatMapRequested: boolean,
 *   heatMapEffective: boolean,
 *   customerAnalyticsRequested: boolean,
 *   customerAnalyticsEffective: boolean,
 *   sessionReplayRequested: boolean,
 *   sessionReplayEffective: boolean,
 *   sessionReplayBlockedReason: string|null,
 *   retentionDays: number,
 *   samplingRatePercent: number,
 *   updatedAtUtc?: string
 * }>}
 */
export const getReplayPolicy = async (organizationId, siteCollectionId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/policy`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders(),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Updates the Replay Policy for a site collection.
 * API #8: PUT /api/v1/heatmap/organizations/{orgId}/site-collections/{siteId}/replay/policy
 *
 * Server caps retentionDays to a maximum of 14 UTC days.
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {{
 *   heatMapEnabled?: boolean,
 *   customerAnalyticsEnabled?: boolean,
 *   sessionReplayRequested?: boolean,
 *   retentionDays?: number,
 *   samplingRatePercent?: number
 * }} policyPayload
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   organizationId: string,
 *   siteCollectionId: string,
 *   heatMapRequested: boolean,
 *   heatMapEffective: boolean,
 *   customerAnalyticsRequested: boolean,
 *   customerAnalyticsEffective: boolean,
 *   sessionReplayRequested: boolean,
 *   sessionReplayEffective: boolean,
 *   sessionReplayBlockedReason: string|null,
 *   retentionDays: number,
 *   samplingRatePercent: number,
 *   updatedAtUtc?: string
 * }>}
 */
export const updateReplayPolicy = async (organizationId, siteCollectionId, policyPayload, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!policyPayload || typeof policyPayload !== 'object') {
    throw new Error('policyPayload object is required');
  }

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/policy`;
  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(policyPayload),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Fetches the scroll reach milestone curve (25%, 50%, 75%, 90%, 100%) for a page.
 * API #15: GET /api/v1/heatmap/organizations/{orgId}/site-collections/{siteId}/scroll-reach
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} params
 * @param {string} params.pageId
 * @param {number} [params.layoutVersion=1]
 * @param {string} params.fromDate
 * @param {string} params.toDate
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{
 *   pageId: string,
 *   layoutVersion: number,
 *   eligibleVisits: number,
 *   milestones: Array<{ depthPercent: number, reachCount: number, reachRate: number }>,
 *   medianScrollDepth: number
 * }>}
 */
export const getScrollReach = async (organizationId, siteCollectionId, {
  pageId,
  layoutVersion = 1,
  fromDate,
  toDate,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!pageId) throw new Error('pageId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({
    pageId,
    layoutVersion: String(layoutVersion),
    fromDate,
    toDate
  });

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/scroll-reach?${params.toString()}`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders(),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Fetches element hover dwell percentiles (p50/p75) excluding touch devices.
 * API #16: GET /api/v1/heatmap/organizations/{orgId}/site-collections/{siteId}/hover-dwell
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} params
 * @param {string} params.pageId
 * @param {number} [params.layoutVersion=1]
 * @param {string} params.fromDate
 * @param {string} params.toDate
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{
 *   pageId: string,
 *   layoutVersion: number,
 *   hoverCapableVisits: number,
 *   touchVisitsExcluded: number,
 *   elements: Array<{
 *     elementId: string,
 *     selector: string,
 *     hoverCount: number,
 *     dwellP50Ms: number,
 *     dwellP75Ms: number,
 *     dwellP90Ms: number,
 *     isTouchUnavailable: boolean
 *   }>
 * }>}
 */
export const getHoverDwell = async (organizationId, siteCollectionId, {
  pageId,
  layoutVersion = 1,
  fromDate,
  toDate,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!pageId) throw new Error('pageId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({
    pageId,
    layoutVersion: String(layoutVersion),
    fromDate,
    toDate
  });

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/hover-dwell?${params.toString()}`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders(),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Fetches section active-visible attention exposure time (p50/p90).
 * Measurement basis: "screen_exposure_not_eye_tracking".
 * API #17: GET /api/v1/heatmap/organizations/{orgId}/site-collections/{siteId}/attention
 *
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} params
 * @param {string} params.pageId
 * @param {number} [params.layoutVersion=1]
 * @param {string} params.fromDate
 * @param {string} params.toDate
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{
 *   pageId: string,
 *   layoutVersion: number,
 *   measurementBasis: string,
 *   sections: Array<{
 *     sectionId: string,
 *     exposureP50Ms: number,
 *     exposureP90Ms: number,
 *     activeVisits: number
 *   }>
 * }>}
 */
export const getAttention = async (organizationId, siteCollectionId, {
  pageId,
  layoutVersion = 1,
  fromDate,
  toDate,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!pageId) throw new Error('pageId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({
    pageId,
    layoutVersion: String(layoutVersion),
    fromDate,
    toDate
  });

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/attention?${params.toString()}`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders(),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Fetches Session Replay playback metadata, state, and 5-minute single-blob Read SAS URLs.
 * (API #14)
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {string} sessionId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   sessionId: string,
 *   status: 'ready' | 'held' | 'expired' | 'incomplete' | 'disabled',
 *   recordingId?: string,
 *   durationSeconds?: number,
 *   segments?: Array<{
 *     segmentId: string,
 *     sequence: number,
 *     chunkType: string,
 *     readSasUrl: string,
 *     expiresAtUtc: string
 *   }>
 * }>}
 */
export const getReplaySession = async (organizationId, siteCollectionId, sessionId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!sessionId) throw new Error('sessionId is required');

  const cleanSessionId = sessionId.includes(':') ? sessionId.split(':').slice(1).join(':') : sessionId;

  // Primary: GET /api/v1/heatmap/organizations/{orgId}/site-collections/{siteId}/replay/sessions/{sessionId}
  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/sessions/${encodeURIComponent(cleanSessionId)}`;

  try {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: { ...getHeaders(), 'Cache-Control': 'no-cache' },
      ...(signal ? { signal } : {})
    });
    return await response.json();
  } catch (primaryErr) {
    // Fallback 1: with raw sessionId (including prefix)
    if (cleanSessionId !== sessionId) {
      try {
        const rawEndpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/sessions/${encodeURIComponent(sessionId)}`;
        const rawRes = await apiRequest(buildApiUrl(rawEndpoint), {
          method: 'GET',
          headers: { ...getHeaders(), 'Cache-Control': 'no-cache' },
          ...(signal ? { signal } : {})
        });
        return await rawRes.json();
      } catch (_) {
        // continue to /replay/{sessionId} fallback
      }
    }

    // Fallback 2: legacy /replay/{sessionId}
    const fallbackEndpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/${encodeURIComponent(cleanSessionId)}`;
    const response = await apiRequest(buildApiUrl(fallbackEndpoint), {
      method: 'GET',
      headers: { ...getHeaders(), 'Cache-Control': 'no-cache' },
      ...(signal ? { signal } : {})
    });
    return await response.json();
  }
};

/**
 * Fetches recent session recordings for a site collection.
 * (API #15)
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} [options]
 * @param {number} [options.take=50]
 * @param {number} [options.skip=0]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array<{
 *   recordingId: string,
 *   sessionId: string,
 *   state?: 'ready' | 'held' | 'expired' | 'incomplete' | 'disabled' | 'sampled_out',
 *   status?: string,
 *   createdAtUtc?: string,
 *   retentionExpiresAtUtc?: string,
 *   startedAtUtc: string,
 *   endedAtUtc?: string,
 *   durationSeconds?: number,
 *   segmentCount?: number,
 *   totalBytes?: number
 * }>>}
 */
export const getReplayRecordings = async (organizationId, siteCollectionId, options = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const take = options.take || 50;
  const skip = options.skip || 0;
  const params = new URLSearchParams({ take: String(take), skip: String(skip) });

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/recordings?${params.toString()}`;

  return withDistributedLockRetry(async () => {
    try {
      const response = await apiRequest(buildApiUrl(endpoint), {
        method: 'GET',
        headers: { ...getHeaders(), 'Cache-Control': 'no-cache' },
        ...(options?.signal ? { signal: options.signal } : {})
      });
      return await response.json();
    } catch (primaryErr) {
      if (primaryErr?.status === 404 || primaryErr?.message?.includes('404')) {
        try {
          const fallbackEndpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/replay/sessions?${params.toString()}`;
          const res = await apiRequest(buildApiUrl(fallbackEndpoint), {
            method: 'GET',
            headers: { ...getHeaders(), 'Cache-Control': 'no-cache' },
            ...(options?.signal ? { signal: options.signal } : {})
          });
          return await res.json();
        } catch (_) {}
      }
      throw primaryErr;
    }
  }, options?.signal);
};

/**
 * Purges telemetry data, click densities, rollups, interactions and optionally snapshots/settings
 * for a specific site collection while leaving the site collection and user flows intact.
 * (DELETE /api/v1/heatmap/organizations/{organizationId}/site-collections/{siteCollectionId}/data)
 *
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} [options]
 * @param {boolean} [options.includeSnapshots=true] - When true, deletes captured DOM HTML/CSS page snapshots.
 * @param {boolean} [options.includeSettings=false] - When false, keeps registered page definitions and configuration.
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   organizationId: string,
 *   siteCollectionId: string,
 *   deletedCount: number,
 *   snapshotsPurged: boolean,
 *   settingsReset: boolean,
 *   lifecycleState: string,
 *   message: string
 * }>}
 */
export const purgeSiteTelemetry = async (organizationId, siteCollectionId, options = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const includeSnapshots = options.includeSnapshots !== undefined ? Boolean(options.includeSnapshots) : true;
  const includeSettings = options.includeSettings !== undefined ? Boolean(options.includeSettings) : false;

  const params = new URLSearchParams({
    includeSnapshots: String(includeSnapshots),
    includeSettings: String(includeSettings)
  });

  const endpoint = `/heatmap/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/data?${params.toString()}`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'DELETE',
    headers: {
      ...getHeaders(),
      'Accept': 'application/json'
    },
    ...(options?.signal ? { signal: options.signal } : {})
  });

  return await response.json();
};



